import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BellRing,
  CalendarClock,
  CheckCheck,
  ChevronDown,
  FileText,
  Heart,
  Megaphone,
  MessageCircle,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Pin,
  Send,
  ThumbsUp,
  Trash2,
  Trophy,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import FileManager from "@/components/files/FileManager";
import EmployeeAvatar from "@/components/communications/EmployeeAvatar";
import ChannelsPanel from "@/components/communications/ChannelsPanel";
import SecureFileImage from "@/components/communications/SecureFileImage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY_DRAFT = {
  title: "",
  body: "",
  category: "Company News",
  priority: "normal",
  pinned: false,
  comments_enabled: true,
  show_responses_to_everyone: true,
  publish_at: "",
  expires_at: "",
  audience: { type: "everyone", values: [] },
  status: "draft",
  celebration: {},
};

const CATEGORY_COLORS = {
  "Company News": "bg-sky-100 text-sky-800",
  Policy: "bg-violet-100 text-violet-800",
  Holiday: "bg-emerald-100 text-emerald-800",
  Achievement: "bg-amber-100 text-amber-900",
  Urgent: "bg-red-100 text-red-800",
  Event: "bg-pink-100 text-pink-800",
};

function toLocalInput(iso) {
  if (!iso) return "";
  return iso.slice(0, 16);
}

function toIso(local) {
  return local ? new Date(local).toISOString() : null;
}

function targetOptions(type, options) {
  if (type === "team") return options.teams.map((item) => [item.id, item.name]);
  if (type === "department")
    return options.departments.map((item) => [item, item]);
  if (type === "location") return options.locations.map((item) => [item, item]);
  if (type === "role")
    return options.roles.map((item) => [item, item.replaceAll("_", " ")]);
  if (type === "people")
    return options.people.map((item) => [item.id, item.name]);
  return [];
}

export default function Communications() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [options, setOptions] = useState(null);
  const [digest, setDigest] = useState(false);
  const [composer, setComposer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("announcements");

  const load = useCallback(async () => {
    try {
      const [feed, optionData, digestData] = await Promise.all([
        api.get("/communications", {
          params: { include_deleted: showDeleted },
        }),
        api.get("/communications/options"),
        api.get("/communications/settings/digest"),
      ]);
      setItems(feed.data.items || []);
      setOptions(optionData.data);
      setDigest(Boolean(digestData.data.email_digest_enabled));
      const wanted = searchParams.get("announcement");
      if (wanted) {
        setSelected(
          (feed.data.items || []).find((item) => item.id === wanted) || null,
        );
      }
    } catch (error) {
      toast.error(apiErr(error));
      setItems([]);
    }
  }, [searchParams, showDeleted]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDigest = async (checked) => {
    try {
      await api.put("/communications/settings/digest", {
        email_digest_enabled: checked,
      });
      setDigest(checked);
      toast.success(checked ? "Email digest enabled" : "Email digest disabled");
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const mutate = async (path, method = "post", data) => {
    try {
      await api[method](path, data);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  if (items === null || options === null) {
    return <CommunicationsLoading />;
  }

  const isSuper = ["super_admin", "founder", "administrator"].includes(
    user?.role,
  );
  const canPost = Boolean(options.can_post);

  const tabBar = (
    <div className="inline-flex rounded-lg border border-border p-1" data-testid="communications-tabs">
      <button
        onClick={() => setView("announcements")}
        data-testid="comms-tab-announcements"
        className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
          view === "announcements" ? "bg-[#255058] text-white" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Announcements
      </button>
      <button
        onClick={() => setView("channels")}
        data-testid="comms-tab-channels"
        className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
          view === "channels" ? "bg-[#255058] text-white" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Channels & DMs
      </button>
    </div>
  );

  if (view === "channels") {
    return (
      <div className="mx-auto max-w-6xl space-y-6" data-testid="communications-page">
        <PageHeader
          title="Communications"
          subtitle="Team channels, direct messages, polls and shout-outs."
          actions={tabBar}
        />
        <ChannelsPanel currentUser={user} />
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-6xl space-y-7"
      data-testid="communications-page"
    >
      <PageHeader
        title="Communications"
        subtitle="Internal company updates, policies, and acknowledgments."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {tabBar}
            <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
              <BellRing className="h-4 w-4 text-[#255058]" />
              <Label htmlFor="digest-toggle" className="text-xs">
                Email digest
              </Label>
              <Switch
                id="digest-toggle"
                checked={digest}
                onCheckedChange={updateDigest}
                data-testid="communications-digest-toggle"
              />
            </div>
            {isSuper && (
              <Button
                variant="outline"
                onClick={() => setShowDeleted((value) => !value)}
                data-testid="communications-deleted-toggle"
              >
                {showDeleted ? "Hide deleted" : "Restore posts"}
              </Button>
            )}
            {canPost && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setComposer(true);
                }}
                data-testid="announcement-create-button"
                className="bg-[#255058] text-white hover:bg-[#1c3d43]"
              >
                <Megaphone className="mr-2 h-4 w-4" />
                New announcement
              </Button>
            )}
          </div>
        }
      />

      <section className="border-y border-[#255058]/15 bg-[#255058]/5 px-5 py-5">
        <div className="flex items-start gap-3">
          <div
            className={
              "grid h-10 w-10 shrink-0 place-items-center rounded-md bg-[#255058] text-white"
            }
          >
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">
              Company updates
            </div>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Important updates are delivered here and through the notification
              bell. Turn on a digest when you want a personal email summary.
            </p>
          </div>
        </div>
      </section>

      <div className="space-y-4">
        {items.length === 0 && (
          <div className="border border-dashed border-border px-6 py-14 text-center">
            <Megaphone className="mx-auto h-8 w-8 text-[#255058]" />
            <div className="mt-4 font-display text-lg font-semibold">
              No announcements yet
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Updates published for your team will appear here.
            </p>
          </div>
        )}
        {items.map((announcement) => (
          <AnnouncementCard
            key={announcement.id}
            announcement={announcement}
            currentUser={user}
            onOpen={() => {
              if (!announcement.is_read) {
                api.post(`/communications/${announcement.id}/read`).then(load);
              }
              setSelected(announcement);
            }}
            onRead={() => mutate(`/communications/${announcement.id}/read`)}
            onAcknowledge={() =>
              mutate(`/communications/${announcement.id}/acknowledge`)
            }
            onReaction={(reaction) =>
              mutate(`/communications/${announcement.id}/reactions`, "post", {
                reaction,
              })
            }
            onDelete={() =>
              mutate(`/communications/${announcement.id}`, "delete")
            }
            onRestore={() =>
              mutate(`/communications/${announcement.id}/restore`)
            }
            onEdit={() => {
              setEditing(announcement);
              setComposer(true);
            }}
          />
        ))}
      </div>

      <AnnouncementComposer
        open={composer}
        options={options}
        editing={editing}
        onClose={() => setComposer(false)}
        onSaved={async (item) => {
          setComposer(false);
          setSelected(item);
          await load();
        }}
      />
      {selected && (
        <AnnouncementDetail
          announcement={selected}
          isSuper={isSuper}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function AnnouncementCard({
  announcement,
  currentUser,
  onAcknowledge,
  onDelete,
  onEdit,
  onOpen,
  onReaction,
  onRead,
  onRestore,
}) {
  const [reacting, setReacting] = useState(false);
  const [comments, setComments] = useState([]);
  const [responseOpen, setResponseOpen] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [comment, setComment] = useState("");
  const loadComments = useCallback(async () => {
    if (!announcement.comments_enabled) return;
    const { data } = await api.get(
      `/communications/${announcement.id}/comments`,
    );
    setComments(data.items || []);
  }, [announcement]);
  useEffect(() => {
    loadComments().catch(() => setComments([]));
  }, [loadComments]);
  const doReaction = async (reaction) => {
    setReacting(true);
    try {
      await onReaction(reaction);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setReacting(false);
    }
  };
  const isDeleted = Boolean(announcement.is_deleted);
  const acknowledgementPercent = announcement.engagement?.recipients
    ? Math.round(
        (announcement.engagement.acknowledged /
          announcement.engagement.recipients) *
          100,
      )
    : 0;
  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/communications/${announcement.id}/comments`, {
        body: comment,
      });
      setComment("");
      await loadComments();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };
  const openResponses = async () => {
    try {
      const { data } = await api.get(
        `/communications/${announcement.id}/receipts`,
      );
      setReceipts(data.items || []);
      setResponseOpen(true);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };
  return (
    <Card
      data-testid={`announcement-card-${announcement.id}`}
      className={
        "overflow-hidden border-l-4 " +
        (announcement.priority === "urgent"
          ? "border-l-red-600"
          : "border-l-[#255058]")
      }
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {announcement.pinned && (
                <Pin className="h-4 w-4 text-[#255058]" />
              )}
              <Badge
                className={
                  CATEGORY_COLORS[announcement.category] ||
                  "bg-muted text-foreground"
                }
              >
                {announcement.category}
              </Badge>
              {announcement.status !== "published" && (
                <Badge variant="outline">{announcement.status}</Badge>
              )}
              {isDeleted && <Badge variant="destructive">Deleted</Badge>}
            </div>
            <button
              type="button"
              onClick={onOpen}
              data-testid={`announcement-open-${announcement.id}`}
              className="mt-3 text-left font-display text-xl font-semibold hover:text-[#255058]"
            >
              {announcement.title}
            </button>
            <div className="mt-2 text-xs text-muted-foreground">
              Posted by {announcement.created_by_name || "Nexreq"}
              {announcement.published_at &&
                ` · ${new Date(announcement.published_at).toLocaleDateString()}`}
            </div>
          </div>
          {announcement.can_manage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  data-testid={`announcement-menu-${announcement.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!isDeleted && (
                  <DropdownMenuItem
                    onClick={onEdit}
                    data-testid={`announcement-edit-${announcement.id}`}
                  >
                    <Pencil className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                {isDeleted ? (
                  <DropdownMenuItem
                    onClick={onRestore}
                    data-testid={`announcement-restore-${announcement.id}`}
                  >
                    <Undo2 className="mr-2 h-4 w-4" /> Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={onDelete}
                    className="text-red-600"
                    data-testid={`announcement-delete-${announcement.id}`}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div
          className="prose prose-sm mt-5 max-w-none text-foreground"
          dangerouslySetInnerHTML={{ __html: announcement.body }}
          data-testid={`announcement-body-${announcement.id}`}
        />
        {announcement.celebration?.template && (
          <CelebrationCard announcement={announcement} />
        )}
        {!isDeleted && (
          <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-border pt-4">
            {[
              ["like", ThumbsUp, "Like"],
              ["celebrate", Trophy, "Celebrate"],
              ["thanks", Heart, "Thanks"],
            ].map(([key, Icon, label]) => (
              <Button
                key={key}
                size="sm"
                variant={
                  announcement.my_reaction === key ? "default" : "outline"
                }
                disabled={reacting}
                onClick={() => doReaction(key)}
                data-testid={`announcement-reaction-${key}-${announcement.id}`}
                className={
                  announcement.my_reaction === key
                    ? "bg-[#255058] hover:bg-[#1c3d43]"
                    : ""
                }
              >
                <Icon className="mr-1.5 h-3.5 w-3.5" />
                {label} {announcement.reaction_counts?.[key] || ""}
                <span className="ml-1 flex -space-x-1">
                  {(announcement.reaction_people?.[key] || [])
                    .slice(0, 3)
                    .map((person) => (
                      <EmployeeAvatar
                        key={person.user_id}
                        className="h-4 w-4 border border-white"
                        fileId={person.profile_photo_id}
                        name={person.name}
                        testId={`reaction-person-${key}-${person.user_id}`}
                      />
                    ))}
                </span>
              </Button>
            ))}
            {announcement.comments_enabled && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onOpen}
                data-testid={`announcement-comments-${announcement.id}`}
              >
                <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Comments
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              {!announcement.is_read && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRead}
                  data-testid={`announcement-read-${announcement.id}`}
                >
                  Mark as read
                </Button>
              )}
              {announcement.is_read && (
                <Badge
                  variant="outline"
                  data-testid={`announcement-read-state-${announcement.id}`}
                >
                  Read
                </Badge>
              )}
              {!announcement.is_acknowledged && (
                <Button
                  size="sm"
                  onClick={onAcknowledge}
                  data-testid={`announcement-acknowledge-${announcement.id}`}
                  className="bg-[#255058] text-white hover:bg-[#1c3d43]"
                >
                  <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> I acknowledge
                </Button>
              )}
            </div>
          </div>
        )}
        {!isDeleted && (
          <div className="mt-4 border-t border-border pt-4">
            <button
              type="button"
              onClick={openResponses}
              data-testid={`announcement-engagement-${announcement.id}`}
              className="text-left text-sm text-muted-foreground hover:text-[#255058]"
            >
              {Object.values(announcement.reaction_counts || {}).reduce(
                (sum, value) => sum + value,
                0,
              )}{" "}
              reactions · {announcement.engagement?.comments || 0} comments ·{" "}
              {announcement.engagement?.acknowledged || 0} acknowledged
            </button>
            <div className="mt-2 h-1.5 overflow-hidden bg-muted">
              <div
                className="h-full bg-[#255058]"
                style={{
                  width: `${acknowledgementPercent}%`,
                }}
              />
            </div>
            <button
              type="button"
              onClick={openResponses}
              className="mt-1 text-xs text-muted-foreground"
            >
              Acknowledged by {announcement.engagement?.acknowledged || 0} of{" "}
              {announcement.engagement?.recipients || 0} · Read by{" "}
              {announcement.engagement?.read || 0} of{" "}
              {announcement.engagement?.recipients || 0}
            </button>
            {announcement.comments_enabled && (
              <div className="mt-4 space-y-3">
                {comments.slice(-3).map((item) => (
                  <div key={item.id} className="flex gap-2 text-sm">
                    <EmployeeAvatar
                      className="h-7 w-7"
                      fileId={item.author?.profile_photo_id}
                      name={item.author?.name}
                      testId={`comment-avatar-${item.id}`}
                    />
                    <div>
                      <span className="font-medium">
                        {item.author?.name || item.user_name}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleString()}
                      </span>
                      <p>{item.body}</p>
                    </div>
                  </div>
                ))}
                {(announcement.engagement?.comments || 0) > 3 && (
                  <Button variant="ghost" size="sm" onClick={onOpen}>
                    View all {announcement.engagement.comments} comments
                  </Button>
                )}
                <Textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      sendComment();
                    }
                  }}
                  placeholder="Add a comment…"
                  data-testid={`announcement-inline-comment-${announcement.id}`}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={sendComment}
                    data-testid={`announcement-inline-comment-submit-${announcement.id}`}
                    className="bg-[#255058] text-white hover:bg-[#1c3d43]"
                  >
                    Comment
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Dialog open={responseOpen} onOpenChange={setResponseOpen}>
        <DialogContent
          data-testid={`announcement-responses-${announcement.id}`}
        >
          <DialogHeader>
            <DialogTitle>Announcement responses</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <ResponseList
              title="Acknowledged"
              items={receipts.filter((item) => item.acknowledged_at)}
            />
            <ResponseList
              title="Pending"
              items={receipts.filter((item) => !item.acknowledged_at)}
              announcement={announcement}
              onRemind={async (userId) => {
                await api.post(
                  `/communications/${announcement.id}/receipts/${userId}/remind`,
                );
                toast.success("Reminder sent");
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ResponseList({ announcement, items, onRemind, title }) {
  return (
    <div>
      <div className="font-display font-semibold">{title}</div>
      <div className="mt-2 space-y-2">
        {items.map((item) => (
          <div key={item.user_id} className="flex items-center gap-2 text-sm">
            <EmployeeAvatar
              className="h-7 w-7"
              fileId={item.profile_photo_id}
              name={item.name}
              testId={`response-avatar-${item.user_id}`}
            />
            <div className="min-w-0 flex-1">
              <div>{item.name}</div>
              <div className="text-xs text-muted-foreground">
                {item.acknowledged_at || item.read_at || "Not read"}
              </div>
            </div>
            {onRemind && !item.acknowledged_at && announcement?.can_manage && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRemind(item.user_id)}
                data-testid={`announcement-remind-${item.user_id}`}
              >
                Send reminder
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AnnouncementComposer({ editing, onClose, onSaved, open, options }) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const editor = useRef(null);
  useEffect(() => {
    if (editing) {
      setDraft({
        ...editing,
        publish_at: toLocalInput(editing.publish_at),
        expires_at: toLocalInput(editing.expires_at),
      });
      if (editor.current) editor.current.innerHTML = editing.body;
    } else {
      setDraft(EMPTY_DRAFT);
      if (editor.current) editor.current.innerHTML = "";
    }
  }, [editing, open]);
  const set = (key, value) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const toggleTarget = (value) => {
    const selected = draft.audience.values.includes(value);
    set("audience", {
      ...draft.audience,
      values: selected
        ? draft.audience.values.filter((item) => item !== value)
        : [...draft.audience.values, value],
    });
  };
  const submit = async () => {
    const body = editor.current?.innerHTML || draft.body;
    if (!draft.title.trim() || !body.replace(/<[^>]*>/g, "").trim()) {
      toast.error("Add an announcement title and body");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        title: draft.title.trim(),
        body,
        publish_at: toIso(draft.publish_at),
        expires_at: toIso(draft.expires_at),
      };
      const response = editing
        ? await api.put(`/communications/${editing.id}`, payload)
        : await api.post("/communications", payload);
      toast.success(editing ? "Announcement updated" : "Announcement saved");
      onSaved(response.data);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };
  const targets = targetOptions(draft.audience.type, options);
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-h-[92vh] max-w-3xl overflow-y-auto"
        data-testid="announcement-composer-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit announcement" : "Create announcement"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Input
            value={draft.title}
            onChange={(event) => set("title", event.target.value)}
            placeholder="Announcement title"
            data-testid="announcement-title-input"
          />
          <div className="rounded-md border border-input">
            <div className="flex gap-1 border-b border-border p-2">
              {[
                ["bold", "B"],
                ["italic", "I"],
                ["insertUnorderedList", "• List"],
              ].map(([command, label]) => (
                <Button
                  key={command}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => document.execCommand(command)}
                  data-testid={`announcement-format-${command}`}
                >
                  {label}
                </Button>
              ))}
            </div>
            <div
              ref={editor}
              contentEditable
              suppressContentEditableWarning
              onInput={(event) => set("body", event.currentTarget.innerHTML)}
              data-testid="announcement-body-editor"
              className="min-h-36 px-3 py-3 text-sm outline-none"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Category">
              <Select
                value={draft.category}
                onValueChange={(value) => set("category", value)}
              >
                <SelectTrigger data-testid="announcement-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.categories.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select
                value={draft.priority}
                onValueChange={(value) => set("priority", value)}
              >
                <SelectTrigger data-testid="announcement-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.priorities.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Publish status">
              <Select
                value={draft.status}
                onValueChange={(value) => set("status", value)}
              >
                <SelectTrigger data-testid="announcement-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="published">Publish now</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Publish date">
              <Input
                type="datetime-local"
                value={draft.publish_at}
                onChange={(event) => set("publish_at", event.target.value)}
                data-testid="announcement-publish-input"
              />
            </Field>
            <Field label="Expiry date">
              <Input
                type="datetime-local"
                value={draft.expires_at}
                onChange={(event) => set("expires_at", event.target.value)}
                data-testid="announcement-expiry-input"
              />
            </Field>
          </div>
          <Field label="Audience">
            <Select
              value={draft.audience.type}
              onValueChange={(type) => set("audience", { type, values: [] })}
            >
              <SelectTrigger data-testid="announcement-audience-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.audience_types.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item === "everyone" ? "Everyone" : item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {draft.audience.type !== "everyone" && (
            <div
              className={
                "flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-md border border-border p-3"
              }
              data-testid="announcement-audience-targets"
            >
              {targets.map(([value, label]) => (
                <Button
                  key={value}
                  size="sm"
                  type="button"
                  variant={
                    draft.audience.values.includes(value)
                      ? "default"
                      : "outline"
                  }
                  onClick={() => toggleTarget(value)}
                  className={
                    draft.audience.values.includes(value)
                      ? "bg-[#255058] hover:bg-[#1c3d43]"
                      : ""
                  }
                >
                  {label}
                </Button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-5">
            <Toggle
              label="Pin announcement"
              checked={draft.pinned}
              onChange={(value) => set("pinned", value)}
              testId="announcement-pinned-toggle"
            />
            <Toggle
              label="Allow comments"
              checked={draft.comments_enabled}
              onChange={(value) => set("comments_enabled", value)}
              testId="announcement-comments-toggle"
            />
            <Toggle
              label="Show responses to everyone"
              checked={draft.show_responses_to_everyone}
              onChange={(value) => set("show_responses_to_everyone", value)}
              testId="announcement-response-visibility-toggle"
            />
          </div>
          <CelebrationEditor draft={draft} options={options} onChange={set} />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="announcement-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="announcement-save-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving
              ? "Saving…"
              : editing
                ? "Save changes"
                : "Save announcement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CelebrationEditor({ draft, onChange, options }) {
  const celebration = draft.celebration || {};
  const enabled = Boolean(celebration.template);
  const update = (key, value) => {
    onChange("celebration", { ...celebration, [key]: value });
  };
  const togglePerson = (employeeId) => {
    const current = celebration.featured_employee_ids || [];
    update(
      "featured_employee_ids",
      current.includes(employeeId)
        ? current.filter((item) => item !== employeeId)
        : [...current, employeeId].slice(0, 10),
    );
  };
  return (
    <div
      className="border-t border-border pt-4"
      data-testid="celebration-editor"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display font-semibold">Celebration card</div>
          <p className="text-xs text-muted-foreground">
            Feature team members in an on-brand announcement card.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(checked) =>
            onChange(
              "celebration",
              checked
                ? {
                    template: "congratulations",
                    theme: "teal",
                    heading: "Congratulations!",
                    message: "Celebrating a Nexreq milestone.",
                    featured_employee_ids: [],
                    gallery_file_ids: [],
                  }
                : {},
            )
          }
          data-testid="celebration-enabled-toggle"
        />
      </div>
      {enabled && (
        <div className="mt-4 grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Card template">
              <Select
                value={celebration.template}
                onValueChange={(value) => update("template", value)}
              >
                <SelectTrigger data-testid="celebration-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.celebration_templates.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item.replaceAll("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Theme">
              <Select
                value={celebration.theme || "teal"}
                onValueChange={(value) => update("theme", value)}
              >
                <SelectTrigger data-testid="celebration-theme-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.celebration_themes.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Input
            value={celebration.heading || ""}
            onChange={(event) => update("heading", event.target.value)}
            placeholder="Card heading"
            data-testid="celebration-heading-input"
          />
          <Textarea
            value={celebration.message || ""}
            onChange={(event) => update("message", event.target.value)}
            placeholder="Celebration message"
            data-testid="celebration-message-input"
          />
          <div className="grid gap-1.5">
            <Label className="text-xs">Featured team members</Label>
            <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto border border-border p-3">
              {options.celebration_people.map((person) => (
                <Button
                  key={person.id}
                  type="button"
                  size="sm"
                  variant={
                    (celebration.featured_employee_ids || []).includes(
                      person.id,
                    )
                      ? "default"
                      : "outline"
                  }
                  onClick={() => togglePerson(person.id)}
                  className={
                    (celebration.featured_employee_ids || []).includes(
                      person.id,
                    )
                      ? "bg-[#255058] hover:bg-[#1c3d43]"
                      : ""
                  }
                >
                  {person.name}
                </Button>
              ))}
            </div>
          </div>
          <CelebrationPreview
            celebration={celebration}
            people={options.celebration_people}
          />
        </div>
      )}
    </div>
  );
}

function CelebrationPreview({ celebration, people }) {
  const selected = people.filter((person) =>
    (celebration.featured_employee_ids || []).includes(person.id),
  );
  return (
    <div
      className="border border-[#255058]/30 bg-[#255058]/5 p-4"
      data-testid="celebration-live-preview"
    >
      <div className="text-xs font-semibold uppercase text-[#255058]">
        Live preview · {celebration.template?.replaceAll("_", " ")}
      </div>
      <div className="mt-2 font-display text-xl font-bold">
        {celebration.heading || "Your celebration heading"}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {celebration.message || "Your celebration message"}
      </p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {selected.map((person) => (
          <Badge key={person.id} variant="outline">
            {person.name}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function CelebrationCard({ announcement }) {
  const celebration = announcement.celebration;
  return (
    <div
      className={
        "mt-5 border border-[#255058]/30 p-5 " +
        `celebration-theme-${celebration.theme || "teal"}`
      }
      data-testid={`celebration-card-${announcement.id}`}
    >
      <div className="text-xs font-semibold uppercase text-[#255058]">
        {celebration.template?.replaceAll("_", " ")}
      </div>
      <div className="mt-2 font-display text-2xl font-bold">
        {celebration.heading || announcement.title}
      </div>
      {celebration.message && (
        <p className="mt-2 text-sm text-muted-foreground">
          {celebration.message}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-4">
        {announcement.featured_people.map((person) => (
          <div key={person.id} className="flex items-center gap-2">
            <EmployeeAvatar
              className="h-11 w-11"
              fileId={person.profile_photo_id}
              name={person.name}
              testId={`celebration-person-${person.id}`}
            />
            <div>
              <div className="text-sm font-semibold">{person.name}</div>
              <div className="text-xs text-muted-foreground">
                {person.designation || "Team member"} ·{" "}
                {person.department || "Nexreq"}
              </div>
            </div>
          </div>
        ))}
      </div>
      {announcement.card_media_file_ids?.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {announcement.card_media_file_ids.map((fileId) => (
            <SecureFileImage
              key={fileId}
              fileId={fileId}
              alt="Celebration card media"
              className="aspect-square w-full object-cover"
              testId={`celebration-media-${fileId}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AnnouncementDetail({ announcement, isSuper, onChanged, onClose }) {
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [receipts, setReceipts] = useState(null);
  const loadComments = useCallback(async () => {
    if (!announcement.comments_enabled) return;
    const { data } = await api.get(
      `/communications/${announcement.id}/comments`,
    );
    setComments(data.items || []);
  }, [announcement]);
  useEffect(() => {
    loadComments().catch(() => setComments([]));
  }, [loadComments]);
  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/communications/${announcement.id}/comments`, {
        body: comment,
      });
      setComment("");
      await loadComments();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };
  const loadReceipts = async () => {
    try {
      const { data } = await api.get(
        `/communications/${announcement.id}/receipts`,
      );
      setReceipts(data.items || []);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[92vh] max-w-4xl overflow-y-auto"
        data-testid="announcement-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle>{announcement.title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6">
          <div>
            <Badge
              className={
                CATEGORY_COLORS[announcement.category] ||
                "bg-muted text-foreground"
              }
            >
              {announcement.category}
            </Badge>
            <div
              className="prose prose-sm mt-4 max-w-none"
              dangerouslySetInnerHTML={{ __html: announcement.body }}
            />
          </div>
          <FileManager
            recordType="announcement"
            recordId={announcement.id}
            canWrite={announcement.can_manage}
            title={
              announcement.celebration?.template
                ? "Card photos & attachments"
                : "Attachments"
            }
          />
          {announcement.comments_enabled && (
            <div className="border-t border-border pt-5">
              <div className="font-display font-semibold">Comments</div>
              <div className="mt-3 space-y-3">
                {comments.map((item) => (
                  <div
                    key={item.id}
                    className="border-l-2 border-[#255058]/35 pl-3 text-sm"
                  >
                    <span className="font-medium">{item.user_name}</span>
                    <p className="mt-1">{item.body}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Add a comment"
                  data-testid="announcement-comment-input"
                />
                <Button
                  onClick={addComment}
                  data-testid="announcement-comment-submit"
                  className="bg-[#255058] text-white hover:bg-[#1c3d43]"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          {announcement.can_manage && (
            <div className="border-t border-border pt-5">
              <Button
                variant="outline"
                onClick={loadReceipts}
                data-testid="announcement-receipts-button"
              >
                <FileText className="mr-2 h-4 w-4" />
                Read receipts
              </Button>
              {receipts && (
                <div className="mt-3 max-h-48 space-y-2 overflow-y-auto text-sm">
                  {receipts.map((item) => (
                    <div
                      key={item.user_id}
                      className="flex justify-between border-b border-border pb-2"
                    >
                      <span>{item.name}</span>
                      <span className="text-muted-foreground">
                        {item.acknowledged_at
                          ? "Acknowledged"
                          : item.read_at
                            ? "Read"
                            : "Not read"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ children, label }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
function Toggle({ checked, label, onChange, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testId}
      />
      <Label className="text-sm">{label}</Label>
    </div>
  );
}
function CommunicationsLoading() {
  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="h-12 w-64 animate-pulse bg-muted" />
      <div className="h-32 animate-pulse bg-muted" />
      <div className="h-64 animate-pulse bg-muted" />
    </div>
  );
}
